import Phaser from 'phaser';
import { AVATAR_SKINS_BY_ID } from '@/config/avatars.config';
import { PlayerAnimator } from '@/game/fx/PlayerAnimator';
import type { AvatarSkinId } from '@/domain/types/avatar';
import type { WorldInteractionController } from '@/game/ui/WorldInteractionController';

const SPRITE_DISPLAY_SIZE = 80; // px — taki sam jak Player (spójna skala awatarów)
const LERP = 0.2;               // wygładzanie pozycji ku celowi z sieci (0..1)

/**
 * RemotePlayer — awatar INNEGO gracza na placu (Etap 5.B.4a + 5.B.4b).
 *
 * Różni się od `Player`:
 *  - BEZ physics body i BEZ InputSystem — pozycję dostaje z sieci (`setTarget`),
 *  - interpolacja (lerp) ku ostatniej pozycji z serwera → płynność mimo
 *    rzadkich update'ów,
 *  - nick nad głową (do rozróżnienia graczy).
 *
 * 5.B.4b: reuse `PlayerAnimator` (ten sam co lokalny gracz) → flip w stronę
 * ruchu, tilt, step-sway, hop/stop, kurz, cień. Prędkość (vx/vy) liczymy z
 * delty interpolowanej pozycji — awatar „ożywa" identycznie jak nasz, bez
 * potrzeby osobnej logiki kierunku. Fallback (brak tekstury) = prostokąt bez
 * animatora (tylko lerp + nick).
 *
 * Y-sort: depth = y (jak dekoracje i lokalny gracz).
 */
export class RemotePlayer {
  private readonly sprite: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
  private readonly label: Phaser.GameObjects.Text;
  private readonly animator: PlayerAnimator | null;
  private targetX: number;
  private targetY: number;
  private prevX: number;
  private prevY: number;
  private labelOffsetY: number;
  private currentSkin: string;
  private currentDisplayName: string;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    avatarSkin: string,
    displayName: string,
  ) {
    this.targetX = x;
    this.targetY = y;
    this.prevX = x;
    this.prevY = y;
    this.currentSkin = avatarSkin;
    this.currentDisplayName = displayName;

    const skin = AVATAR_SKINS_BY_ID[avatarSkin as AvatarSkinId];
    const textureKey = skin?.art.textureKey;
    const useSprite = textureKey != null && scene.textures.exists(textureKey);

    if (useSprite) {
      const img = scene.add.image(x, y, textureKey!).setOrigin(0.5, 1.0);
      const maxSide = Math.max(img.width, img.height);
      const baseScale = (SPRITE_DISPLAY_SIZE / maxSide) * (skin!.art.scale ?? 1);
      img.setScale(baseScale);
      this.sprite = img;
      // Ten sam animator co lokalny gracz — spójny look + flip/tilt/cień/kurz.
      this.animator = new PlayerAnimator(scene, img, baseScale, x, y);
    } else {
      const rect = scene.add.rectangle(x, y, 40, 48, 0x4a6cf7).setOrigin(0.5, 1.0);
      rect.setStrokeStyle(3, 0x000000);
      this.sprite = rect;
      this.animator = null;
    }
    this.sprite.setDepth(y);
    // Offset nicka liczony RAZ z bazowej wysokości (animator pulsuje skalę —
    // czytanie displayHeight co klatkę dawałoby drganie etykiety).
    this.labelOffsetY = this.sprite.displayHeight + 4;

    // Nick nad głową — biały tekst z ciemnym obrysem, czytelny na każdym tle.
    this.label = scene.add
      .text(x, y - this.labelOffsetY, displayName, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#ffffff',
        stroke: '#1a1a1a',
        strokeThickness: 3,
      })
      .setOrigin(0.5, 1.0)
      .setDepth(y + 1);
  }

  /**
   * Czyni awatar interaktywnym (5.C.1 — szybki hold gracza → propozycja
   * handlu). Mysz zachowuje natychmiastowy klik. Hit-area = bounds sprite'a.
   * `pointerout` nie anuluje gestu, bo awatar sam porusza się pod palcem.
   */
  makeClickable(interactions: WorldInteractionController, onClick: () => void): void {
    this.sprite.setInteractive({ useHandCursor: true });
    interactions.bind(this.sprite, onClick, {
      profile: 'quickHold',
      cancelOnPointerOut: false,
    });
  }

  get x(): number {
    return this.sprite.x;
  }

  get y(): number {
    return this.sprite.y;
  }

  /** Nowa pozycja docelowa z sieci (interpolujemy ku niej w `update`). */
  setTarget(x: number, y: number, _dir: string): void {
    this.targetX = x;
    this.targetY = y;
    // _dir z serwera ignorujemy — flip/animację wyznacza animator z prędkości
    // (delta interpolowanej pozycji), spójnie z lokalnym graczem.
  }

  /** Live-sync skina i nicku bez respawnu obiektu ani utraty interakcji. */
  updateProfile(avatarSkin: string, displayName: string): void {
    if (displayName !== this.currentDisplayName) {
      this.currentDisplayName = displayName;
      this.label.setText(displayName);
    }
    if (avatarSkin === this.currentSkin) return;
    this.currentSkin = avatarSkin;

    const skin = AVATAR_SKINS_BY_ID[avatarSkin as AvatarSkinId];
    const textureKey = skin?.art.textureKey;
    if (
      !textureKey
      || !(this.sprite instanceof Phaser.GameObjects.Image)
      || !this.sprite.scene.textures.exists(textureKey)
    ) {
      return;
    }
    this.sprite.setTexture(textureKey);
    const maxSide = Math.max(this.sprite.width, this.sprite.height);
    const baseScale = (SPRITE_DISPLAY_SIZE / maxSide) * (skin.art.scale ?? 1);
    this.sprite.setScale(baseScale);
    this.animator?.setBaseScale(baseScale);
    this.labelOffsetY = this.sprite.displayHeight + 4;
  }

  /**
   * Lerp ku celowi + animacja. `time`/`dt` (sekundy) z PlazaScene.update —
   * animator potrzebuje prędkości w px/s (dt w sekundach) do tilt/kurz/flip.
   */
  update(time: number, dt: number): void {
    this.sprite.x += (this.targetX - this.sprite.x) * LERP;
    this.sprite.y += (this.targetY - this.sprite.y) * LERP;

    const vx = dt > 0 ? (this.sprite.x - this.prevX) / dt : 0;
    const vy = dt > 0 ? (this.sprite.y - this.prevY) / dt : 0;
    this.prevX = this.sprite.x;
    this.prevY = this.sprite.y;

    this.sprite.setDepth(this.sprite.y);
    if (this.animator) {
      this.animator.update(time, vx, vy, this.sprite.x, this.sprite.y);
    }

    this.label.setPosition(this.sprite.x, this.sprite.y - this.labelOffsetY);
    this.label.setDepth(this.sprite.y + 1);
  }

  destroy(): void {
    this.animator?.destroy();
    this.sprite.destroy();
    this.label.destroy();
  }
}
